// Utils
import cn from '../../../../utils/cn';
import { normalizeUrl } from '../../../../utils/url';

// Styles
import directEditCommon from '../DirectEditCommon.module.css';

// Domain
import {
  getDirectEditType,
  isCellDisplayValueEmpty,
  isDirectEditDoubleClickType,
} from '../../../../domain/tableDirectEdit/tableDirectEditUseCase';

// Components
import DirectEditBoolean from '../DirectEditBoolean/DirectEditBoolean';
import { getDirectEditShellProps } from '../getDirectEditShellClassName';
import DirectEditValueSlot from '../DirectEditValueSlot/DirectEditValueSlot';

/**
 * Direct-edit cell at rest: same markup as DirectEditWrapper, no hooks.
 * Activation (click / double-click) is delegated to the parent so only the
 * active cell mounts the editor and its mutation.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {TableCellValueObjectCustom} props.cellValue
 * @param {() => void} props.onActivate
 * @returns {JSX.Element}
 */
function DirectEditCellStatic({
  children,
  cellValue,
  onActivate,
}) {
  const directEditType = getDirectEditType(cellValue?.directEdit);
  const hasDirectEdit = directEditType !== null;
  const { className: shellClassName, style: shellStyle } = getDirectEditShellProps(cellValue);
  const showLinkIcon = isCellDisplayValueEmpty(cellValue);

  if (directEditType === 'actionChoice' && hasDirectEdit) {
    return (
      <div className={shellClassName} style={shellStyle}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onActivate();
          }}
          className={cn([
            directEditCommon['direct-edit-value-control'],
            directEditCommon['direct-edit-value-control-action'],
          ])}
        >
          <DirectEditValueSlot action={null}>{children}</DirectEditValueSlot>
        </button>
      </div>
    );
  }

  if (directEditType === 'boolean' && hasDirectEdit) {
    return (
      <DirectEditBoolean
        cellValue={cellValue}
        onClick={onActivate}
        isPendingSave={false}
      />
    );
  }

  const hasBasicUrl = cellValue?.basicUrl != null;

  if (hasBasicUrl && !hasDirectEdit) {
    const handleBasicUrlClick = () => {
      const normalizedUrl = normalizeUrl(cellValue.basicUrl);
      window.open(normalizedUrl, '_blank');
    };

    return (
      <div className={shellClassName} style={shellStyle}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleBasicUrlClick();
          }}
          className={cn([
            directEditCommon['direct-edit-value-control'],
            directEditCommon['direct-edit-value-control-link'],
          ])}
        >
          <DirectEditValueSlot action={showLinkIcon ? 'link' : null}>{children}</DirectEditValueSlot>
        </button>
      </div>
    );
  }

  if (hasDirectEdit) {
    const isDoubleClick = isDirectEditDoubleClickType(directEditType);
    return (
      <div className={shellClassName} style={shellStyle}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!isDoubleClick) onActivate();
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (isDoubleClick) onActivate();
          }}
          className={cn([
            directEditCommon['direct-edit-value-control'],
            directEditCommon['direct-edit-value-control-editable'],
          ])}
        >
          <DirectEditValueSlot action={null}>{children}</DirectEditValueSlot>
        </button>
      </div>
    );
  }

  return <div className={shellClassName} style={shellStyle}>{children}</div>;
}

export default DirectEditCellStatic;
